import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Film, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const KEYS = [
  { k: "video.editor.export.720p",         label: "Export 720p",   hint: "Per export job" },
  { k: "video.editor.export.1080p",        label: "Export 1080p",  hint: "Per export job" },
  { k: "video.editor.export.2k",           label: "Export 2K",     hint: "Per export job" },
  { k: "video.editor.export.4k",           label: "Export 4K",     hint: "Per export job" },
  { k: "video.editor.ai_command",          label: "AI command",    hint: "Per applied AI edit" },
  { k: "video.editor.transcribe_per_min",  label: "Auto-captions", hint: "Per minute of audio" },
  { k: "file.upload",                      label: "Media upload",  hint: "Per file uploaded to Media Library" },
];

export default function AdminVideoEditor() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      const tp = r.data?.tool_pricing || {};
      const seed = {};
      KEYS.forEach(({ k }) => { seed[k] = tp[k] ?? 0; });
      setPricing(seed);
    }).catch(() => toast.error("Could not load settings"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const numeric = Object.fromEntries(Object.entries(pricing).map(([k, v]) => [k, Math.max(0, parseInt(v || 0, 10))]));
      const merged = { ...(settings?.tool_pricing || {}), ...numeric };
      await api.patch("/admin/settings", { tool_pricing: merged });
      toast.success("Video Editor pricing updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (user?.role !== "super_admin") {
    return <div className="p-8 text-white/50">Super admin access required.</div>;
  }

  return (
    <div className="p-8 max-w-[900px]">
      <div className="kr-overline text-[#8A52FF]">[ SUPER ADMIN · VIDEO EDITOR ]</div>
      <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-ve-title">Video Editor · Credit Pricing</h1>
      <p className="text-white/55 text-sm mt-1.5 max-w-2xl">Configure the credit cost of every editor operation. Users see these costs in the editor UI and are charged from the credit ledger. Changes apply immediately.</p>

      <div className="mt-8 kr-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Film className="h-4 w-4 text-[#00E5FF]" />
          <div className="kr-overline">TOOL PRICING</div>
        </div>
        <div className="space-y-3">
          {KEYS.map(({ k, label, hint }) => (
            <div key={k} className="flex items-center gap-3 border border-white/5 bg-[#050505] px-3 py-2.5" data-testid={`admin-ve-price-${k}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm">{label}</div>
                <div className="text-[10px] text-white/40 mt-0.5 font-mono">{k} · {hint}</div>
              </div>
              <Input
                type="number"
                min={0}
                value={pricing[k] ?? 0}
                onChange={(e) => setPricing((p) => ({ ...p, [k]: e.target.value }))}
                className="w-24 bg-[#0A0A0E] border-white/10 text-right font-mono"
              />
              <span className="text-xs text-white/40 w-8">cr</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-[#8A52FF] hover:bg-[#8A52FF]/90" data-testid="admin-ve-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
